import {
  getEligiblePlanCtaAmount,
  getEligiblePlanCtaId
} from '../components/elite/dashboard/eliteOnboardingFocusPlan'
import { isYearlyPlanTenure } from '../features/new-elite-landing/eliteOnboardingPlanTenure'
import type {
  EliteOnboardingEligiblePlan,
  EliteOnboardingStatusResponse,
  EliteSubscriptionPlanDetails
} from '../types/misc.types'

export type EliteEventExtras = {
  amount?: string
  cta_name?: string
}

/**
 * Buy-CTA analytics extras for the Elite funnel: `amount` (price shown on the CTA)
 * and `cta_name` (applicable plan id(s)). Pure — derives only from the advisory
 * status the caller already has. It does NOT read caches or fetch; screens hand it
 * their status (directly, or via `EliteBuyCtaExtrasProvider`).
 *
 * Price source is a priority chain, NOT a guess about payload "version":
 *   1. `eligiblePlans` rendered as buy CTAs — the monthly/yearly cards + sticky button.
 *   2. Upgrade flow (already Elite) — the single upgrade-target plan.
 *   3. Legacy / family — the single `userSubscriptionDetails` membership fee.
 * The first source that exists wins, because that is the price the user actually sees.
 */
export function getEliteBuyCtaEventExtras(status?: EliteOnboardingStatusResponse | null): EliteEventExtras {
  if (!status) return {}

  // 1. Buy / renewal plans that are actually rendered as CTAs. Other tenures
  // (e.g. an upgrade-target row bundled in eligiblePlans) are excluded so the
  // reported amount matches what the user saw. Two plans (monthly + yearly)
  // stay comma-joined in the same order for both fields.
  const buyPlans = (status.eligiblePlans ?? []).filter(isShownBuyElitePlan)
  if (buyPlans.length > 0) {
    return {
      amount: buyPlans.map((plan) => getEligiblePlanCtaAmount(plan)).join(','),
      cta_name: joinPlanIds(buyPlans.map((plan) => getEligiblePlanCtaId(plan)))
    }
  }

  // 2. Upgrade flow: no buy cards, but a single upgrade-target plan is offered.
  const upgradePlan =
    status.subscriptionDetails?.upgradeDetails?.upgradePlanDetails ??
    status.subscriptionDetails?.upgradePlanDetails
  if (upgradePlan) {
    return {
      amount: toAmountString(getPlanDetailsFeeExGst(upgradePlan)),
      cta_name: idOrUndefined(upgradePlan.effectivePlanId ?? upgradePlan.basePlanId)
    }
  }

  // 3. Legacy v1 / family landing: one sticky button, one fee, optional plan id.
  return {
    amount: toAmountString(status.userSubscriptionDetails?.membershipFees?.amount),
    cta_name: idOrUndefined(status.userSubscriptionDetails?.planId)
  }
}

/** Only monthly + yearly plans are shown as buy CTAs; those are the funnel prices. */
function isShownBuyElitePlan(plan: EliteOnboardingEligiblePlan): boolean {
  // Plan cards use effective tenure when an offer changes duration, else base.
  const tenure = plan?.effectivePlanTenure ?? plan?.basePlanTenure
  if (isYearlyPlanTenure(tenure)) return true
  return tenure?.period === 'MONTH'
}

/** Effective offer fee when present, else base fee. `undefined` when neither exists. */
function getPlanDetailsFeeExGst(plan: EliteSubscriptionPlanDetails): number | undefined {
  return plan?.effectivePlanFees?.amount ?? plan?.basePlanFees?.amount
}

function toAmountString(amount?: number | null): string | undefined {
  return amount == null ? undefined : String(amount)
}

function idOrUndefined(id?: string | null): string | undefined {
  return id ? id : undefined
}

function joinPlanIds(ids: string[]): string | undefined {
  const present = ids.filter(Boolean)
  return present.length > 0 ? present.join(',') : undefined
}
